import { FC } from 'react';
import { Alert, Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type { ScheduleSettingsSection } from './config';

type OverviewSummary = {
  readiness: {
    blockers: string[];
    score: number;
  };
};

type AdvancedReadOnlyWorkspaceProps = {
  section: ScheduleSettingsSection;
  tenantQuery: string;
  overview: OverviewSummary | null;
};

const liveSectionLinks: Array<{ id: string; label: string; path: string }> = [
  { id: 'imports', label: 'Imports', path: '/scheduling/settings/imports' },
  { id: 'locations', label: 'Locations', path: '/scheduling/settings/locations' },
  { id: 'services', label: 'Services', path: '/scheduling/settings/services' },
  { id: 'providers', label: 'People', path: '/scheduling/settings/providers' },
];

const sectionGuidance: Record<
  string,
  {
    statusLabel: string;
    statusTone: 'warning' | 'info' | 'success';
    actionTitle: string;
    actionText: string;
  }
> = {
  eligibility: {
    statusLabel: 'Unavailable',
    statusTone: 'warning',
    actionTitle: 'Get the prerequisite records stable first',
    actionText: 'Eligibility depends on current provider, service, and location records. Use the live registries below before expecting rule-level coverage mapping here.',
  },
  'time-off': {
    statusLabel: 'Unavailable',
    statusTone: 'warning',
    actionTitle: 'Keep availability constraints in the existing source process',
    actionText: 'CARES does not edit PTO or admin-time blocks on this route yet. Use the current external source of truth and keep imported roster data current.',
  },
  targets: {
    statusLabel: 'Unavailable',
    statusTone: 'warning',
    actionTitle: 'Treat targets as external until policy publishing lands',
    actionText: 'Fairness targets and balancing preferences should stay in the approved external process. This page is a readiness reminder, not an active scheduling control.',
  },
  rules: {
    statusLabel: 'Unavailable',
    statusTone: 'warning',
    actionTitle: 'Do not infer active scheduling policy from this screen',
    actionText: 'Hard and soft scheduling constraints are still tracked outside CARES. Keep the upstream inputs organized so the future rule workspace can consume them safely.',
  },
  'coverage-templates': {
    statusLabel: 'Unavailable',
    statusTone: 'warning',
    actionTitle: 'Prepare the coverage inputs before template editing opens',
    actionText: 'Coverage patterns still depend on stable roles, services, and locations. Use the live sections to keep those inputs clean before template controls are enabled.',
  },
  validation: {
    statusLabel: 'Readiness summary',
    statusTone: 'info',
    actionTitle: 'Use the blocker summary as today’s preflight',
    actionText: 'Validation reports the readiness blockers identified for this Schedule Group. It does not create policy or grant schedule-launch approval on its own.',
  },
};

const toneAlertMap: Record<'warning' | 'info' | 'success', string> = {
  warning: 'warning',
  info: 'light',
  success: 'success',
};

const buildSectionPath = (path: string, tenantQuery: string) =>
  tenantQuery.trim() === '' ? path : `${path}?${tenantQuery}`;

const AdvancedReadOnlyWorkspace: FC<AdvancedReadOnlyWorkspaceProps> = ({
  section,
  tenantQuery,
  overview,
}) => {
  const guidance = sectionGuidance[section.id] ?? sectionGuidance.validation;
  const blockers = section.id === 'validation' ? overview?.readiness.blockers ?? [] : [];
  const isReady = section.id === 'validation' && blockers.length === 0;
  const statusTone = isReady ? 'success' : guidance.statusTone;

  return (
    <Card className="custom-card h-100">
      <Card.Header className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
        <div>
          <div className="card-title mb-0">{section.navLabel}</div>
          <p className="text-muted fs-13 mb-0">{section.workspaceDescription}</p>
        </div>
        <Badge
          bg=""
          className={`align-self-start align-self-md-center bg-${statusTone}-transparent text-${statusTone}`}
        >
          {isReady ? 'No blockers reported' : guidance.statusLabel}
        </Badge>
      </Card.Header>
      <Card.Body className="d-flex flex-column gap-4">
        <Alert variant={toneAlertMap[statusTone]} className="mb-0 border">
          <div className="fw-semibold mb-1">{guidance.actionTitle}</div>
          <div className="mb-0">{guidance.actionText}</div>
        </Alert>

        <div>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <p className="fw-semibold mb-0">Open a live section</p>
            {section.id === 'validation' && overview ? (
              <span className="text-muted fs-12">Readiness score: {overview.readiness.score}%</span>
            ) : null}
          </div>
          <p className="text-muted fs-13 mb-3">
            Use the sections that already have working controls instead of waiting on unavailable policy editors.
          </p>
          <div className="d-flex flex-wrap gap-2">
            {liveSectionLinks.map((link) => (
              <Link
                key={link.id}
                to={buildSectionPath(link.path, tenantQuery)}
                className="btn btn-light btn-wave text-start"
              >
                Open {link.label}
              </Link>
            ))}
          </div>
        </div>

        {section.id === 'validation' ? (
          blockers.length > 0 ? (
            <div className="border rounded-3 p-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h6 className="mb-1">Critical blocker summary</h6>
                  <p className="text-muted fs-13 mb-0">
                    Resolve these before treating the tenant as ready to generate schedules.
                  </p>
                </div>
                <Badge bg="" className="bg-warning-transparent text-warning">
                  {blockers.length} blocking item{blockers.length === 1 ? '' : 's'}
                </Badge>
              </div>
              <ul className="mb-0 ps-3 d-flex flex-column gap-2">
                {blockers.map((blocker) => (
                  <li key={blocker} className="text-muted">
                    {blocker}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <Alert variant="success" className="mb-0 border">
              No readiness blockers are currently reported for this Schedule Group.
            </Alert>
          )
        ) : (
          <div className="border rounded-3 p-3 bg-light">
            <p className="fw-semibold mb-2">What to do now</p>
            <ul className="mb-0 ps-3 d-flex flex-column gap-2">
              {section.checklist.map((item) => (
                <li key={item} className="text-muted">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AdvancedReadOnlyWorkspace;
